import * as zod from 'zod';

import { ActionRequiredError, Step } from '@sb/remote-control/types';

import { CNCOperateAutodoorStepDatabase } from '../CNCOperateAutodoor/CNCOperateAutodoorStepDatabase';
import {
  getActionByID,
  getDevice,
  getWorkholding,
} from '../toRoutineRunnerHelpers';

export namespace CNCOperateWorkholdingStepDatabase {
  export const name = 'Operate workholding';
  export const description =
    'Operate the vise, chuck or other workholding on a CNC machine';
  export const deviceKinds = ['CNCMachine'] as const;
  export const librarySection = Step.LibrarySection.InterfaceWithMachines;
  export const librarySort = '2';

  export const argumentKind = 'CNCOperateWorkholding';
  export const featureFlag = 'newCNCIntegration';

  export const Arguments = zod.object({
    argumentKind: zod.literal(argumentKind),
    deviceID: zod.string().default(''),
    workholdingID: zod.number().default(0),
    actionID: zod.string().default(''),
  });

  export type Arguments = zod.infer<typeof Arguments>;

  export const validator: Step.Validator = ({
    stepConfiguration,
    ioOutputs,
    equipment,
  }) => {
    const args = stepConfiguration?.args;

    if (args?.argumentKind !== argumentKind) {
      return;
    }

    const machineConfig = getDevice(args.deviceID, equipment);

    const workholding = getWorkholding(machineConfig, args.workholdingID);

    const action = getActionByID(workholding, args.actionID);

    if (action == null) {
      throw new ActionRequiredError({
        kind: 'invalidConfiguration',
        message: 'Action is required',
        fieldId: 'actionID',
      });
    }

    CNCOperateAutodoorStepDatabase.validateSafetyOutputUsage(ioOutputs, action);
  };

  export const toRoutineRunner: Step.ToRoutineRunner = ({
    stepConfiguration: { args },
    stepData,
    equipment,
  }) => {
    if (args?.argumentKind !== argumentKind) {
      throw new TypeError(`Expected argument kind ${argumentKind}`);
    }

    const machineConfig = getDevice(args.deviceID, equipment);

    const workholding = getWorkholding(machineConfig, args.workholdingID);

    getActionByID(workholding, args.actionID);

    return {
      ...stepData,
      stepKind: 'CNCOperateWorkholding',
      args,
    };
  };
}

CNCOperateWorkholdingStepDatabase satisfies Step.StepKindInfo;
